import { ComponentRef, computed, Injectable, signal, Type, ViewContainerRef } from '@angular/core';

@Injectable({
    providedIn: 'root',
})
/**
 * MUST BE INITIALIZED
 * Before use, a reference to an ng-template must be provided
 * Then the service can be referenced from anywhere in the app to open a custom modal, or modal-like component
 */
export class CustomPortalService {
    /**
     * Target reference
     * This is where we'll render the content
     */
    private readonly _targetVcr = this.initVcr();
    /**
     * Reference to the rendered component
     */
    private readonly _componentRef = signal<ComponentRef<any> | undefined>(undefined);

    readonly isOpen = computed(() => !!this._componentRef());

    init(vcr: ViewContainerRef) {
        this._targetVcr.set(vcr);
    }

    open<T>(component: Type<T>): ComponentRef<T> {
        const vcr = this._targetVcr.get();
        vcr.clear();

        // render content inside the target
        const ref = vcr.createComponent(component);

        // clean and update reference to content
        this.updateComponentRef(ref);

        return ref;
    }

    close() {
        this.updateComponentRef(undefined);
    }

    private updateComponentRef(ref: ComponentRef<any> | undefined) {
        this._componentRef.update(prevRef => {
            if (prevRef) prevRef.destroy();
            return ref;
        });
    }

    /** Closure to ensure the ViewContainerRef is initialized */
    private initVcr() {
        let ref: ViewContainerRef | undefined;

        return {
            get: () => {
                if (!ref) throw new Error('ViewContainerRef @ CustomPortalService not initialized');
                return ref;
            },
            set(value: ViewContainerRef | undefined) {
                ref = value;
            },
        };
    }
}
